import * as Yup from 'yup';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { Stack, Button, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
// components
import { FormProvider, RHFTextField } from '../../components/hook-form';
// utils
import { errorHandler } from '../../utils/errorHandler';
import { track } from '../../components-custom/Analytics';

// ----------------------------------------------------------------------

export default function MobileVerificationForm() {
  const { user, checkOTP } = useAuth();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const [candEdit, setCanEdit] = useState(!user?.mobile);
  const [codeId, setCodeId] = useState(null);

  const FormSchema = Yup.object().shape({
    mobile: Yup.string()
      .matches(/^(\+\d{1,3}[- ]?)?\d{10}$/, translate('auth.mobileVerification.schema.mobileValid'))
      .required(translate('auth.mobileVerification.schema.mobile')),
    ...(codeId && {
      code: Yup.string().matches(/^\d{4}$/, translate('auth.mobileVerification.schema.code')),
    }),
  });

  const defaultValues = {
    mobile: user?.mobile || '',
    code: '',
  };

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const body = { action: codeId ? 'verify' : 'send' };
      if (body.action === 'send') body.mobile = data.mobile;
      else {
        body.code = data.code;
        body.id = codeId;
      }
      const request = await checkOTP(body);
      if (body.action === 'send') {
        setCanEdit(false);
        setCodeId(request.id);
      }
      track.event('MobileVerification.checkOTP');
    } catch (error) {
      enqueueSnackbar(
        errorHandler(error, translate(`auth.mobileVerification.message.${codeId ? 'verify' : 'send'}.error`)),
        { variant: 'error' }
      );
    }
  };

  const handleCanEdit = async () => {
    setCanEdit(true);
    setCodeId(null);
  };

  if (user && user.mobile_verified) return <Navigate to={PATH.root} />;
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <RHFTextField
          name="mobile"
          label={translate('auth.mobileVerification.mobile')}
          disabled={!candEdit}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button type="button" onClick={handleCanEdit} sx={{ mr: -0.5 }}>
                  {translate('auth.mobileVerification.edit')}
                </Button>
              </InputAdornment>
            ),
          }}
        />
        {codeId && (
          <RHFTextField name="code" label={translate('auth.mobileVerification.code')} autoComplete="one-time-code" />
        )}
        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
          {translate(`auth.mobileVerification.${codeId ? 'submit' : 'send'}`)}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
